import { Location } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DeliveryConstants } from '../../app-core/constants/common-constants';
import { TrackingFormPayload } from '../../app-core/data-model/tracking-data-model';
import { OrderStatus } from '../../app-core/enums/common-enum';
import { LoaderService } from '../../app-core/services/loader.service';
import { StockStatus } from './../../app-core/enums/common-enum';
import { CommonService } from './../../app-core/services/common.service';
import { NavigationStateService } from '../../app-core/services/navigation-state.service';

@Component({
	selector: 'app-landing-page',
	templateUrl: './landing-page.component.html',
	styleUrl: './landing-page.component.scss',
})
export class LandingPageComponent implements OnInit {
	@ViewChild('deliveryPostcodeInput') deliveryPostcodeInput!: ElementRef<HTMLInputElement>;
	@ViewChild('deliveryPostcode', { static: false }) deliveryPostcodeField!: NgModel;
	private hasQueryParams = false;
	deliveryConstants: typeof DeliveryConstants = DeliveryConstants;
	src = 'assets/images/website-header.png';
	abtSrc = 'assets/images/arrowxl-vehicles.png';
	flowType: 'booking' | 'tracking' = 'tracking';

	errorMessage: string = '';
	trackingFormPayload: TrackingFormPayload = {
		trackingNumber: '',
		orderNumber: '',
		deliveryPostcode: '',
		requestedBy: '1',
	};

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private location: Location,
		private commonService: CommonService,
		private loaderService: LoaderService,
		private navigationStateService: NavigationStateService
	) { }

	ngOnInit(): void {
		// HANDLE ROUTE PARAM FIRST
		this.route.paramMap.subscribe(params => {
			const upi = params.get('upi');

			if (upi) {
				// Redirect to query param URL
				this.router.navigate(['/tracking'], {
					queryParams: { upi: upi },
					replaceUrl: true
				});
				return;
			}
		});

		const params = this.route.snapshot.queryParams;

		// If query params exist → Booking flow
		if (Object.keys(params).length > 0) {
			this.flowType = 'booking';
		} else {
			this.flowType = 'tracking';
		}

		// Prefill trackingNumber from query param 'upi'
		this.route.queryParams.subscribe((params) => {
			this.hasQueryParams = Object.keys(params).length > 0;
			if (params['upi']) {
				this.trackingFormPayload.trackingNumber = params['upi'];
			}
			if (params['postcode']) {
				this.trackingFormPayload.deliveryPostcode = params['postcode'];
			}
		});
		this.errorMessage = this.navigationStateService.getErrorMessage() || '';
	}

	ngAfterViewInit() {
		setTimeout(() => {
			if (
				this.hasQueryParams && // only if URL had params
				!this.trackingFormPayload.deliveryPostcode // but postcode is missing
			) {
				// Autofocus on postcode
				this.deliveryPostcodeInput.nativeElement.focus();
				// Force error state immediately
				this.deliveryPostcodeField?.control.markAsTouched();
				this.deliveryPostcodeField?.control.markAsDirty();
			}
		});
	}

	onFormSubmit() {
		const { trackingNumber, orderNumber, deliveryPostcode, requestedBy } = this.trackingFormPayload;

		const query = new URLSearchParams({
			upi: trackingNumber || '',
			postcode: deliveryPostcode || '',
			requestedBy: requestedBy || '',
		}).toString();
		this.location.replaceState('/tracking', query);
		this.loaderService.show();
		const payload = {
			orderNumber: orderNumber,
			upi: trackingNumber,
			postcode: deliveryPostcode,
			requestedBy: requestedBy,
		};
		this.commonService.getWismoDetails(payload).subscribe({
			next: (res) => {
				const wismoDetail = res?.arrowxl_wismo_response?.wismo_detail;
				const upi = wismoDetail?.order_line_info?.order_line_info_item[0]?.upi;
				const deliveryDate = wismoDetail?.delivery_date ?? '';
				const hasDebrief = !!wismoDetail.debrief_reason;

				if (
					wismoDetail?.order_status === OrderStatus.BOOKED &&
					wismoDetail?.stock_status === StockStatus.AWAITINGSTOCK && (wismoDetail?.delivery_date === '' || (wismoDetail?.delivery_date && hasDebrief))
				) {
					this.router.navigate(['/order-details'], { queryParams: { upi: upi, postcode: deliveryPostcode } });
				} else if (
					wismoDetail?.order_status === OrderStatus.AWAITQUINTIQBOOK &&
					(wismoDetail?.stock_status === StockStatus.STOCKRECEIVED || wismoDetail?.stock_status === StockStatus.AWAITINGSTOCK) &&
					deliveryDate !== ''
				) {
					this.router.navigate(['/tracking-history'], { queryParams: { upi: upi, postcode: deliveryPostcode } });
				} else if (
					wismoDetail?.order_status === OrderStatus.B4P &&
					(wismoDetail?.stock_status === StockStatus.STOCKRECEIVED || wismoDetail?.stock_status === StockStatus.AWAITINGSTOCK)
				) {
					this.router.navigate(['/order-details'], { queryParams: { upi: upi, postcode: deliveryPostcode } });
				} else if (wismoDetail?.order_status === OrderStatus.AWAITINGSTOCK && wismoDetail?.stock_status === StockStatus.AWAITINGSTOCK && hasDebrief) {
					this.router.navigate(['/order-details'], { queryParams: { upi: upi, postcode: deliveryPostcode } });
				} else if (
					[
						OrderStatus.AWAITINGSTOCK,
						OrderStatus.ROUTEPLANNING,
						OrderStatus.PICKING,
						OrderStatus.PREADVICECREATED,
						OrderStatus.LOADED,
						OrderStatus.INPROGRESS,
						OrderStatus.COLLECTED,
						OrderStatus.DELIVERED,
						OrderStatus.STOCKRECEIVED,
						OrderStatus.AUDITCLOSED,
						OrderStatus.CANCELLED,
					].includes(wismoDetail?.order_status) &&
					([StockStatus.AWAITINGSTOCK, StockStatus.STOCKRECEIVED].includes(wismoDetail.stock_status) ||
						wismoDetail.stock_status === '')
				) {
					this.router.navigate(['/tracking-history'], { queryParams: { upi: upi, postcode: deliveryPostcode } });
				} else {
					this.errorMessage =
						res?.arrowxl_wismo_response?.wismo_detail?.request_error_description || this.deliveryConstants.notFoundError;
				}
				this.loaderService.hide();
			},
			error: (err) => {
				this.errorMessage = err;
				this.loaderService.hide();
			},
		});
	}
}
